// ── progressHeat ──────────────────────────────────────────────────────────────
// Series-completion progress bars warm up as you approach the finale: cool blue at
// the start ("just getting into it"), gold in the middle, hot orange→red near the
// end — with a flame at the leading edge on the home stretch (Jason 2026-07-22).

const STOPS = [
  [0.0,  [91, 156, 246]],  // --blue-soft : cool, early
  [0.35, [85, 239, 196]],  // mint
  [0.6,  [245, 200, 66]],  // gold (--amber-ish)
  [0.8,  [255, 140, 66]],  // orange
  [1.0,  [255, 77, 77]],   // hot red, finale heat
]

const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0))
const lerp = (a, b, t) => Math.round(a + (b - a) * t)

// Solid heat color for a completion fraction (0..1) — the "temperature" at that point.
export function heatColor(frac) {
  const f = clamp01(frac)
  for (let i = 1; i < STOPS.length; i++) {
    if (f <= STOPS[i][0]) {
      const [f0, c0] = STOPS[i - 1]
      const [f1, c1] = STOPS[i]
      const t = (f - f0) / ((f1 - f0) || 1)
      return `rgb(${lerp(c0[0], c1[0], t)}, ${lerp(c0[1], c1[1], t)}, ${lerp(c0[2], c1[2], t)})`
    }
  }
  return 'rgb(255,77,77)'
}

// Fill gradient: cool base → the current heat, so the bar visibly warms as it grows.
export function heatFill(frac) {
  return `linear-gradient(90deg, rgb(91,156,246), ${heatColor(frac)})`
}

// ── Flame growth ─────────────────────────────────────────────────────────────
// The flame used to be a late binary marker: nothing at all until 85%, then a fixed 11px
// flame that vanished again at 100%. The intended design — and what Jason expected to see
// — is a flame that APPEARS once you're genuinely into a series and GROWS as you burn
// through it, so the bar reads as heat building rather than a badge switching on
// (2026-07-27).
//
// Starts at 0.35, which is where the gradient itself leaves cool blue for mint — the
// flame and the colour ramp now describe the same story instead of disagreeing. Below
// that a couple of episodes into a long show shouldn't look like a blaze.
export const FLAME_START = 0.35

// 0 below the threshold, else 0..1 across the remaining range. Drives BOTH size and glow
// so growth is felt, not just measured.
export function flameGrowth(frac) {
  const f = clamp01(frac)
  if (f < FLAME_START) return 0
  return (f - FLAME_START) / (1 - FLAME_START)
}

// Kept for compatibility / anything wanting the old "final push" band specifically.
export function isHomeStretch(frac) {
  const f = clamp01(frac)
  return f >= 0.85 && f < 1
}
